<xemex-page-header
  [title]="installationGroupStore.detail()?.name ?? ('sidebarMenu.installationGroups' | translate)"
  [subtitle]="installationGroupStore.detail()?.ownerPartnerName ?? ''"
  [breadcrumbs]="breadcrumbs()"
  [loading]="installationGroupStore.isLoadingDetail()"
  [withBackground]="true"
  [withTabBar]="true"
  [icon]="'workspaces'"
>
  <!-- The tab bar reads the route's tab only when it is created, and the Installations tab joins
       once the profile has loaded. A new tab bar then lets a link straight to it highlight it. -->
  @if (showInstallations()) {
    <xemex-tab-bar
      [tabs]="tabsWithInstallations()"
      [defaultTab]="0"
      (tabChange)="onTabChange($event)"
    ></xemex-tab-bar>
  } @else {
    <xemex-tab-bar
      [tabs]="tabs()"
      [defaultTab]="0"
      (tabChange)="onTabChange($event)"
    ></xemex-tab-bar>
  }
</xemex-page-header>

<!-- General tab -->
@if (selectedTab() === 'general') {
  <xemex-page-body [scrollable]="true">
    <xemex-card-grid>
      <!-- General card -->
      <xemex-card
        [title]="'installationGroups.detail.general' | translate"
        [loading]="installationGroupStore.isLoadingDetail()"
        [actionLabel]="canManage() ? ('general.btnEdit' | translate) : undefined"
        actionIcon="edit"
        (action)="onEdit()"
      >
        <div class="grid grid-cols-2 gap-x-6 gap-y-4">
          <xemex-detail-field [label]="'installationGroups.table.owner' | translate" [truncate]="true">
            @if (installationGroupStore.detail()?.ownerPartnerOid) {
              <xemex-link-button (click)="onViewOwnerPartner()">
                {{ installationGroupStore.detail()?.ownerPartnerName }}
              </xemex-link-button>
            } @else {
              {{ installationGroupStore.detail()?.ownerPartnerName }}
            }
          </xemex-detail-field>
          <xemex-detail-field [label]="'installationGroups.table.status' | translate" [truncate]="true">
            @if (installationGroupStore.detail()?.status; as status) {
              {{ 'installationGroups.edit.statuses.' + status | translate }}
            }
          </xemex-detail-field>
          <xemex-detail-field [label]="'installationGroups.table.accessVia' | translate" [truncate]="true">
            @if (
              installationGroupStore.detail()?.accessVia === 'partnership' &&
              installationGroupStore.detail()?.viaPartnershipOid
            ) {
              <xemex-link-button (click)="onViewPartnership()">
                {{ installationGroupStore.detail()?.accessVia }}
              </xemex-link-button>
            } @else {
              {{ installationGroupStore.detail()?.accessVia }}
            }
          </xemex-detail-field>
          <xemex-detail-field [label]="'installationGroups.detail.description' | translate" [truncate]="true">
            {{ installationGroupStore.detail()?.description }}
          </xemex-detail-field>
          <xemex-detail-field [label]="'installationGroups.table.createdAt' | translate" [truncate]="true">
            {{ installationGroupStore.detail()?.createdAt | date: 'dd-MM-yyyy' }}
          </xemex-detail-field>
          <xemex-detail-field [label]="'installationGroups.detail.createdBy' | translate" [truncate]="true">
            {{ installationGroupStore.detail()?.createdByUserName }}
          </xemex-detail-field>
        </div>
      </xemex-card>

      <!-- Partnerships card -->
      <xemex-card
        [title]="'installationGroups.detail.partnerships' | translate"
        [loading]="installationGroupStore.isLoadingDetail()"
        [actionLabel]="
          canManagePartnerships() ? ('installationGroups.addPartnership.title' | translate) : undefined
        "
        actionIcon="add"
        (action)="onAddPartnership()"
      >
        @if (!partnerships().length) {
          <xemex-no-items-found [compact]="true" />
        } @else {
          <div class="flex flex-col divide-y divide-gray-100">
            @for (partnership of partnerships(); track partnership.partnershipOid) {
              <xemex-list-row
                icon="handshake"
                [title]="partnershipPartnerName(partnership)"
                [subtitle]="partnership.partnershipType + ' · ' + partnership.status"
                [clickable]="true"
                (rowClick)="onViewPartnershipById(partnership.partnershipOid)"
              >
                @if (canManagePartnerships()) {
                  <button
                    listRowActions
                    mat-icon-button
                    type="button"
                    [matTooltip]="'installationGroups.removePartnership.tooltip' | translate"
                    (click)="onRemovePartnership(partnership, $event)"
                  >
                    <mat-icon class="text-gray-400 !text-lg">delete_outline</mat-icon>
                  </button>
                }
                <mat-icon listRowActions class="text-gray-400 !text-lg">chevron_right</mat-icon>
              </xemex-list-row>
            }
          </div>
        }
      </xemex-card>

      <!-- Audit log card -->
      @if (canViewAuditLog()) {
        <xemex-card
          [title]="'installationGroups.detail.auditLog' | translate"
          [loading]="installationGroupStore.isLoadingAuditLog()"
        >
          @if (!auditLog().length) {
            <xemex-no-items-found [compact]="true" />
          } @else {
            <div class="flex flex-col divide-y divide-gray-100">
              @for (entry of auditLog(); track entry.auditLogId) {
                <xemex-list-row icon="history" [title]="eventLabel(entry.eventType)" align="start">
                  @if (entry.actorUserName || entry.actorPartnerName) {
                    <div class="text-xs text-gray-500 truncate">
                      {{ entry.actorUserName ?? entry.actorPartnerName }}
                      @if (entry.actorUserName && entry.actorPartnerName) {
                        · {{ entry.actorPartnerName }}
                      }
                    </div>
                  }
                  @if (updatedFields(entry); as fields) {
                    @if (fields.length) {
                      <div class="mt-1 flex flex-col gap-0.5 text-xs text-gray-500">
                        @for (field of fields; track field.label) {
                          <div class="truncate">
                            <span class="font-medium text-gray-600">{{ field.label }}:</span>
                            {{ field.value }}
                          </div>
                        }
                      </div>
                    }
                  }
                  @if (entry.subjectPartnerOid || entry.partnershipOid || resourceLink(entry)) {
                    <div class="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1 text-xs">
                      @if (entry.subjectPartnerOid) {
                        <xemex-link-button
                          icon="business"
                          (click)="onViewPartner(entry.subjectPartnerOid)"
                        >
                          {{ entry.subjectPartnerName ?? entry.subjectPartnerOid }}
                        </xemex-link-button>
                      }
                      @if (entry.partnershipOid) {
                        <xemex-link-button
                          icon="handshake"
                          (click)="onViewPartnershipById(entry.partnershipOid)"
                        >
                          {{ 'sidebarMenu.partnerships' | translate }}
                        </xemex-link-button>
                      }
                      @if (resourceLink(entry); as resource) {
                        <xemex-link-button [icon]="resource.icon" (click)="onViewResource(entry)">
                          {{ resource.label }}
                        </xemex-link-button>
                      }
                    </div>
                  }
                  <span listRowActions class="text-xs text-gray-400 whitespace-nowrap">
                    {{ entry.createdAt | date: 'dd-MM-yyyy HH:mm' }}
                  </span>
                </xemex-list-row>
              }
            </div>
            @if (auditLogCount() > pageSize) {
              <xemex-table-pagination
                [page]="auditLogPage()"
                [totalPages]="auditLogTotalPages()"
                [total]="auditLogCount()"
                [pageSize]="pageSize"
                [compact]="true"
                (pageChange)="onAuditLogPageChange($event)"
              ></xemex-table-pagination>
            }
          }
        </xemex-card>
      }
    </xemex-card-grid>
  </xemex-page-body>
}

<!-- Devices tab -->
@if (selectedTab() === 'devices') {
  <xemex-page-body [scrollable]="true">
    <xemex-card-grid>
      <xemex-card
        [title]="'installationGroups.detail.devices' | translate"
        [actionLabel]="canManage() ? ('installationGroups.addDevice.title' | translate) : undefined"
        actionIcon="add"
        (action)="onAddDevice()"
      >
        @if (!devices().devices.length) {
          <xemex-no-items-found [compact]="true" />
        } @else {
          <div class="flex flex-col divide-y divide-gray-100">
            @for (dev of devices().devices; track dev.deviceId) {
              <xemex-list-row
                icon="router"
                [title]="(dev.ldn || dev.deviceId).toString()"
                [subtitle]="dev.deviceTypeName"
                [clickable]="true"
                (rowClick)="onViewDevice(dev.deviceId)"
              >
                <span listRowActions class="text-xs text-gray-500">
                  {{ 'installationGroups.devices.relations.' + dev.relation | translate }}
                </span>
                @if (canManage() && dev.relation === 'direct') {
                  <button
                    listRowActions
                    mat-icon-button
                    type="button"
                    [matTooltip]="'installationGroups.removeDevice.tooltip' | translate"
                    (click)="onRemoveDevice(dev.deviceId, dev.ldn || dev.deviceId.toString(), $event)"
                  >
                    <mat-icon class="text-gray-400 !text-lg">delete_outline</mat-icon>
                  </button>
                }
              </xemex-list-row>
            }
          </div>
          @if (deviceCount() > pageSize) {
            <xemex-table-pagination
              [page]="devicesPage()"
              [totalPages]="deviceTotalPages()"
              [total]="deviceCount()"
              [pageSize]="pageSize"
              [compact]="true"
              (pageChange)="onDevicesPageChange($event)"
            ></xemex-table-pagination>
          }
        }
      </xemex-card>
    </xemex-card-grid>
  </xemex-page-body>
}

<!-- Locations tab -->
@if (selectedTab() === 'locations') {
  <xemex-page-body [scrollable]="true">
    <xemex-card-grid>
      <xemex-card
        [title]="'installationGroups.detail.locations' | translate"
        [actionLabel]="canManage() ? ('installationGroups.addLocation.title' | translate) : undefined"
        actionIcon="add"
        (action)="onAddLocation()"
      >
        @if (!locations().length) {
          <xemex-no-items-found [compact]="true" />
        } @else {
          <div class="flex flex-col divide-y divide-gray-100">
            @for (loc of locations(); track loc.locationId) {
              <xemex-list-row
                icon="home_work"
                [title]="loc.address || loc.postalcode || ''"
                [subtitle]="loc.postalCity"
                [clickable]="true"
                (rowClick)="onViewLocation(loc.locationId)"
              >
                @if (canManage()) {
                  <button
                    listRowActions
                    mat-icon-button
                    type="button"
                    [matTooltip]="'installationGroups.removeLocation.tooltip' | translate"
                    (click)="
                      onRemoveLocation(
                        loc.locationId,
                        loc.address || loc.postalcode || loc.locationId.toString(),
                        $event
                      )
                    "
                  >
                    <mat-icon class="text-gray-400 !text-lg">delete_outline</mat-icon>
                  </button>
                }
                <mat-icon listRowActions class="text-gray-400 !text-lg">chevron_right</mat-icon>
              </xemex-list-row>
            }
          </div>
          @if (locationCount() > pageSize) {
            <xemex-table-pagination
              [page]="locationsPage()"
              [totalPages]="locationTotalPages()"
              [total]="locationCount()"
              [pageSize]="pageSize"
              [compact]="true"
              (pageChange)="onLocationsPageChange($event)"
            ></xemex-table-pagination>
          }
        }
      </xemex-card>
    </xemex-card-grid>
  </xemex-page-body>
}

<!-- Installations tab -->
@if (selectedTab() === 'installations' && showInstallations()) {
  <xemex-page-body [scrollable]="true">
    <xemex-card-grid>
      <xemex-group-installations
        [rows]="installationRows()"
        [total]="installationCount()"
        [page]="installationsPage()"
        [pageSize]="pageSize"
        [loading]="installationGroupStore.isLoadingInstallations()"
        (pageChange)="onInstallationsPageChange($event)"
        (viewInstallation)="onViewInstallation($event)"
        (viewDevice)="onViewDevice($event)"
        (viewLocation)="onViewLocation($event)"
      />
    </xemex-card-grid>
  </xemex-page-body>
}
